package pl.decks.personal.components.widgets.hasdiagram

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.setVariable
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.CssStyleVariant
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.deg
import org.jetbrains.compose.web.css.div
import org.jetbrains.compose.web.css.minus
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.plus
import org.jetbrains.compose.web.css.times
import pl.decks.personal.components.widgets.hasdiagram.HASDiagramStylesheet.Diagram
import pl.decks.personal.components.widgets.hasdiagram.HASDiagramStylesheet.ConnectionLines
import pl.decks.personal.components.widgets.hasdiagram.HASDiagramStylesheet.Node
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.sin

@Composable
fun HubAndSpokeDiagram(
    numberOuters: Int? = null,
    startAngle: Float = 0f,
    nodeVariant: CssStyleVariant<Node.Kind>? = null,
    lineVariant: CssStyleVariant<ConnectionLines.Kind>? = null,
    lineDirection: HASLineDirection = HASLineDirection.INWARDS,
    modifier: Modifier = Modifier,
    content: HASDiagramScope.() -> Unit,
) {
    val scope = HASDiagramScope(
        fixedNumberOuters = numberOuters,
    ).also {
        it.content()
        it.resolveNodes()
    }
    val resolvedNumberOuters = scope.resolvedNumberOuters

    val innerNode = scope.resolvedNodes[HASNodePosition.Inner]
    val outerNodes = scope.resolvedNodes.filterKeys { it is HASNodePosition.Outer }
    Box(modifier = Diagram.Container.toModifier().then(modifier)) {
        outerNodes.forEach { (position, _) ->
            val pos = (position as HASNodePosition.Outer).pos
            val lineState = scope.resolvedLineFor(pos)
            if (lineState.visible) HASLine(
                index = pos,
                total = resolvedNumberOuters,
                startAngle = startAngle,
                fraction = lineState.fraction,
                direction = lineState.direction ?: lineDirection,
                variant = lineState.variant ?: lineVariant,
                modifier = lineState.modifier,
            )
        }
        outerNodes.forEach { (position, node) ->
            HASNode(
                position = position,
                total = resolvedNumberOuters,
                startAngle = startAngle,
                variant = node.variant ?: nodeVariant,
                modifier = node.modifier,
                content = node.content,
            )
        }
        innerNode?.let { inner ->
            HASNode(
                position = HASNodePosition.Inner,
                total = resolvedNumberOuters,
                startAngle = startAngle,
                variant = inner.variant ?: nodeVariant,
                modifier = inner.modifier,
                content = inner.content,
            )
        }
    }
}

@Composable
private fun HASLine(
    index: Int,
    total: Int,
    startAngle: Float,
    fraction: Float,
    direction: HASLineDirection,
    variant: CssStyleVariant<ConnectionLines.Kind>? = null,
    modifier: Modifier = Modifier,
) {
    val angle = startAngle + (index * 360f / total) - 180f
    Box(
        modifier = ConnectionLines.Style.toModifier(variant)
            .styleModifier {
                property("transform", "translate(0%, -50%) rotate(${angle}deg) translate(0%, 50%)")
            }
            .then(modifier),
        contentAlignment = if (direction == HASLineDirection.INWARDS) Alignment.BottomCenter else Alignment.TopCenter,
    ) {
        Box(ConnectionLines.Guide.toModifier())
        Box(Modifier.height(fraction.times(100).percent)) {
            Box(ConnectionLines.Main.toModifier())
        }
    }
}

@Composable
private fun HASNode(
    position: HASNodePosition,
    total: Int,
    startAngle: Float,
    variant: CssStyleVariant<Node.Kind>? = null,
    modifier: Modifier = Modifier,
    content: @Composable () -> Unit,
) {
    val nodeModifier = when (position) {
        HASNodePosition.Inner -> Modifier
            .styleModifier {
                property("top", "50%")
                property("left", "50%")
                property("transform", "translate(-50%, -50%)")
            }
            .setVariable(
                HASDiagramStylesheet.Vars.Internal.nodeContentSize,
                HASDiagramStylesheet.Vars.innerNodeSize.value(),
            )

        is HASNodePosition.Outer -> {
            val angle = (startAngle + (position.pos * 360f / total))
            val angleRad = angle * PI / 180.0
            val spokeRadius =
                50.percent - (HASDiagramStylesheet.Vars.nodeSize.value() + HASDiagramStylesheet.Vars.innerNodeSize.value()) / 2
            val top = 50.percent - spokeRadius * cos(angleRad).toFloat()
            val left = 50.percent + spokeRadius * sin(angleRad).toFloat()
            Modifier
                .styleModifier {
                    property("top", top.toString())
                    property("left", left.toString())
                    property("transform", "translate(-50%, -50%)")
                }
                .setVariable(
                    HASDiagramStylesheet.Vars.Internal.nodeStartingAngle,
                    (angle.plus(180f) % 360f).deg,
                )
                .setVariable(
                    HASDiagramStylesheet.Vars.Internal.nodeContentSize,
                    HASDiagramStylesheet.Vars.nodeSize.value(),
                )
        }
    }

    Box(
        modifier = Node.Back.toModifier(variant)
            .then(nodeModifier)
            .then(modifier),
        contentAlignment = Alignment.Center,
    ) {
        Box(Node.Content.toModifier(), contentAlignment = Alignment.Center) {
            content()
        }
    }
}
